import { supabase } from '@/integrations/supabase/client';

export interface ImageUploadResult {
  url: string;
  filename: string;
}

/**
 * Validates if the provided string is a valid image URL
 */
export const validateImageUrl = (url: string): { isValid: boolean; error?: string } => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'URL cannot be empty' };
  }

  // Basic URL validation
  try {
    new URL(url);
  } catch {
    return { isValid: false, error: 'Invalid URL format' };
  }

  // Check if URL looks like an image
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
  const hasImageExtension = imageExtensions.test(url);
  
  // Allow URLs without obvious image extensions (could be from services like Unsplash, etc.)
  // Just warn if it doesn't look like an image URL
  if (!hasImageExtension && !url.includes('unsplash') && !url.includes('pexels') && !url.includes('pixabay')) {
    // Don't reject, just warn - many image services don't have file extensions in URLs
    console.warn('URL may not be an image:', url);
  }

  return { isValid: true };
};

/**
 * Validates if a file is a supported image format
 */
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB

  if (!supportedFormats.includes(file.type)) {
    return {
      isValid: false,
      error: 'Unsupported file format. Please use JPG, PNG, GIF, or WebP.'
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size too large. Maximum size is 5MB.'
    };
  }

  return { isValid: true };
};

/**
 * Uploads an image file to Supabase storage via Edge Function
 */
export const uploadImageToSupabase = async (
  file: File
): Promise<ImageUploadResult> => {
  try {
    // Validate file before upload
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Call the upload Edge Function
    const { data, error } = await supabase.functions.invoke('upload-image', {
      body: formData
    });

    if (error) {
      console.error('Upload Edge Function error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    if (!data.success) {
      console.error('Upload failed:', data.error);
      throw new Error(data.error || 'Upload failed');
    }

    return {
      url: data.url,
      filename: data.filename
    };
  } catch (error) {
    console.error('Image upload error:', error);
    throw error instanceof Error ? error : new Error('Upload failed');
  }
};

/**
 * Checks if an image URL is accessible
 */
export const checkImageAccessibility = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok && response.headers.get('content-type')?.startsWith('image/');
  } catch {
    return false;
  }
}; 